import styled from '@emotion/styled';
import { Prismic, PrismicContext, PrismicLink } from '@landsbankinn/prismic';
import {
  device,
  Divider,
  typography,
  mobileGutterMargin,
  shouldForwardProp,
} from '@landsbankinn/ui';
import { useRouter } from 'next/router';
import React, { forwardRef, useContext, useState } from 'react';
import { animated } from 'react-spring';
import type { ImgSource, ImgAr } from '../../components/img/Img';
import { Img } from '../../components/img/Img';

export const HorizontalHost = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<any>`
  display: block;
  position: relative;
  width: auto;

  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};
  background-color: ${(props) =>
    props.backgroundAlt
      ? props.theme.colors.backgroundAlt
      : props.theme.colors.background};
  display: flex;

  flex-direction: column;
  max-width: 1179px;

  cursor: pointer;

  ${mobileGutterMargin}

  @media ${device.tablet} {
    flex-direction: ${(props) => (!props.reverse ? 'row' : 'row-reverse')};
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  &:hover div img {
    transform: scale(1.025);
  }
`;

const Image = styled(Img)`
  width: 100%;

  @media ${device.tablet} {
    width: 50%;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: ${(props) => `0 ${props.theme.containerGutter}px`};

  @media ${device.mobile} {
    padding: ${(props) => `0 ${props.theme.breakpoints.sm.gutter}px`};
  }

  @media ${device.tablet} {
    width: 50%;
    padding: 0;
  }
`;

const Title = styled.h2`
  ${typography.h3};
  margin-bottom: 3px;
`;

const Copy = styled.div`
  ${typography.body}
  margin-bottom: 24px;
`;

const Center = styled.div<{ isProductCard?: boolean }>`
  margin: 0 auto;
  padding: ${(props) => (props.isProductCard ? '36px 0 0' : '2em 0')};
  /* margin-bottom: 36px; */
  max-width: 100%;

  @media ${device.tablet} {
    padding: 2.5em;
  }

  @media ${device.desktop} {
    padding: 3.5em;
  }
`;

export interface CardProps {
  link?: Prismic.Link;
  title?: string;
  description: React.ReactNode;
  image?: ImgSource;
  ar?: ImgAr;
  reverse?: boolean;
  backgroundAlt?: boolean;
  ref: React.LegacyRef<any>;
  style?: any;
  isProductCard?: boolean;
  onMouseEnter?: any;
  onMouseLeave?: any;
}

export const HorizontalCard = forwardRef(function HCard(
  {
    link,
    title,
    description,
    image,
    ar = 'ar_1_1',
    reverse = false,
    backgroundAlt = false,
    isProductCard = false,
    onMouseEnter,
    onMouseLeave,
    ...rest
  }: CardProps,
  ref,
) {
  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, link);
  const [hover, setHover] = useState(false);

  const onEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }
    return router.push(url.href ?? '', url.as);
  };

  return (
    <HorizontalHost
      reverse={reverse}
      backgroundAlt={backgroundAlt}
      {...rest}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
      ref={ref}
    >
      {!!image && <Image percentage={0.5} src={image} ar={ar} />}
      <Body>
        <Center isProductCard={isProductCard}>
          <Title>
            <PrismicLink value={link} title={title}>
              <a>{title}</a>
            </PrismicLink>
          </Title>
          <Divider />
          <Copy>{description}</Copy>
          {/* <PrismicLink value={link} title={title}>
              <ArrowButton hideText hover={hover} />
            </PrismicLink> */}
        </Center>
      </Body>
    </HorizontalHost>
  );
});
